import { observer } from 'mobx-react-lite';
import { Button, Typography, TextField, IconButton, Tooltip, Divider } from '@material-ui/core';
import { Stack, Alert, Box, Grid } from '@mui/material';
import { Refresh } from '@material-ui/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { CopilotStore } from '../CopilotStore';
import { ConfigMigrationStatus } from '../models';
import StepConfirmation, { ConfirmationDialogProps, defaultConfirmationDialogProps } from '../../controls/StepConfirmation';
import StepConfirmationWithState, { ConfirmationDialogPropsWithState } from '../../controls/StepConfirmationWithState';
import AquantWarningIcon from '../../../assets/aq-warning.svg';
import { EmbeddingModel, PdfReadMethod } from '../types';
import { embeddingModelItems, pdfReadMethodItems } from '../configUtils';
import { CopilotInputItems, InputItem } from '../components/CopilotInputItems';

type ConfigMigrationProps = {
    store: CopilotStore
    unlockPdfReadMethods: boolean
}

export const ConfigMigration = observer(({ store, unlockPdfReadMethods }: ConfigMigrationProps) => {
    const {
        selectedCustomer: customer,
        backend,
        user_email,
        tenantConfig
    } = store

    const [embeddingModel, setEmbeddingModel] = useState<EmbeddingModel>('text-embedding-3-large');
    const [pdfReadMethod, setPdfReadMethod] = useState<PdfReadMethod>('msr');
    const [isInProgress, setIsInProgress] = useState<boolean>(false);
    const [dialogProps, setDialogProps] = useState<ConfirmationDialogProps | undefined>(undefined);
    const [clearDataDialogProps, setClearDataDialogProps] = useState<ConfirmationDialogPropsWithState[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [configMigrationStatus, setConfigMigrationStatus] = useState<ConfigMigrationStatus | undefined>(undefined);
    const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(false);

    const embeddingModels = useMemo(() => embeddingModelItems(pdfReadMethod, tenantConfig?.provider), [pdfReadMethod, tenantConfig?.provider])
    useEffect(() =>{
        const filteredModel = embeddingModels.find(model => model && model.value === embeddingModel)
        if (!filteredModel){    
            setEmbeddingModel(embeddingModels[0].value as EmbeddingModel)
        }else{
            setEmbeddingModel(filteredModel.value as EmbeddingModel)
        }
    },[embeddingModels, embeddingModel])

    const fetchConfigMigrationStatus = async (tenant_name: string) => {
        try {
            setIsLoadingStatus(true);
            const status = await backend!.getConfigMigrationStatus(tenant_name);
            setConfigMigrationStatus(status);
        } catch (error) {
            console.error('Error fetching config migration status:', error);
            setConfigMigrationStatus(undefined);
        } finally {
            setIsLoadingStatus(false);
        }
    };

    const startConfigMigration = async (tenant_name: string, embedding_model: string, pdf_read_method: string) => {
        try {
            await backend!.startConfigMigration(tenant_name, embedding_model, pdf_read_method, user_email);
            // Refresh status after starting migration
            await fetchConfigMigrationStatus(tenant_name);
        } catch (error) {
            console.error('Error starting config migration:', error);
            throw error;
        }
    };

    const abortConfigMigration = async (tenant_name: string) => {
        try {
            await backend!.abortConfigMigration(tenant_name, user_email);
            // Refresh status after aborting migration
            await fetchConfigMigrationStatus(tenant_name);
        } catch (error) {
            console.error('Error aborting config migration:', error);
            throw error;
        }
    };

    const resumeConfigMigration = async (tenant_name: string) => {
        try {
            await backend!.resumeConfigMigration(tenant_name, user_email);
            // Refresh status after resuming migration
            await fetchConfigMigrationStatus(tenant_name);
        } catch (error) {
            console.error('Error resuming config migration:', error);
            throw error;
        }
    };

    const swapConfigMigration = async (tenant_name: string) => {
        try {
            await backend!.swapConfigMigration(tenant_name, user_email);
            // Refresh status after swapping migration
            await fetchConfigMigrationStatus(tenant_name);
        } catch (error) {
            console.error('Error swapping config migration:', error);
            throw error;
        }
    };

    const clearConfigMigration = async (tenant_name: string) => {
        try {
            await backend!.clearConfigMigration(tenant_name, user_email);
            // Refresh status after clearing migration
            await fetchConfigMigrationStatus(tenant_name);
        } catch (error) {
            console.error('Error clearing config migration:', error);
            throw error;
        }
    };

    const copyNonMigrated = async (tenant_name: string) => {
        try {
            await backend!.copyNonMigrated(tenant_name);
            // Refresh status after copying non-migrated documents
            await fetchConfigMigrationStatus(tenant_name);
        } catch (error) {
            console.error('Error copying non-migrated documents:', error);
            throw error;
        }
    };

    useEffect(() => {
        if (customer) {
            fetchConfigMigrationStatus(customer.name);
        }
    }, [customer]);

    useEffect(() => {
        if (configMigrationStatus) {
            setIsInProgress(configMigrationStatus.status === 'InProgress' || configMigrationStatus.status === 'Initializing' || configMigrationStatus.status === 'Swapping');
        }
    }, [configMigrationStatus]);

    // Refresh status periodically when migration is initializing or swapping
    useEffect(() => {
        if ((configMigrationStatus?.status === 'Initializing' || configMigrationStatus?.status === 'Swapping') && customer) {
            const interval = setInterval(() => {
                fetchConfigMigrationStatus(customer.name);
            }, 5000); // Refresh every 5 seconds

            return () => clearInterval(interval);
        }
    }, [configMigrationStatus?.status, customer]);

    const handleMigrate = () => {
        setDialogProps({
            ...defaultConfirmationDialogProps,
            title: 'Start Config Migration?',
            content: (
                <>
                    <div style={{ display: 'flex', marginBottom: 15, marginTop: 15 }}>
                        <img src={AquantWarningIcon} alt='warning-icon' style={{ marginRight: 5 }} />
                        <Typography variant='subtitle1'>
                            This will start migrating the configuration with the selected settings.
                        </Typography>
                    </div>
                    <Typography variant='body1' style={{marginTop: 15}}>
                        Are you sure you want to proceed?
                    </Typography>
                </>
            ),
            onYes: async () => {
                try {
                    await startConfigMigration(customer!.name, embeddingModel, pdfReadMethod);
                    setErrorMessage(null);
                } catch (error) {
                    console.error('Failed to start config migration:', error);
                    setErrorMessage('Failed to start migration. Please try again.');
                }
                setDialogProps(undefined);
            },
            yesLabel: 'Yes',
            closeLabel: 'No',
        });
    };

    const handleCancel = () => {
        setDialogProps({
            ...defaultConfirmationDialogProps,
            title: 'Cancel Migration?',
            content: (
                <>
                    <div style={{ display: 'flex', marginBottom: 15, marginTop: 15 }}>
                        <img src={AquantWarningIcon} alt='warning-icon' style={{ marginRight: 5 }} />
                        <Typography variant='subtitle1'>
                            This will cancel the migration initialization process.
                        </Typography>
                    </div>
                    <Typography variant='body1' style={{marginTop: 15}}>
                        Are you sure you want to proceed?
                    </Typography>
                </>
            ),
            onYes: async () => {
                try {
                    await abortConfigMigration(customer!.name);
                    setErrorMessage(null);
                } catch (error) {
                    console.error('Failed to cancel config migration:', error);
                    setErrorMessage('Failed to cancel migration. Please try again.');
                }
                setDialogProps(undefined);
            },
            yesLabel: 'Cancel',
            closeLabel: 'No',
        });
    };

    const handleAbort = () => {
        setDialogProps({
            ...defaultConfirmationDialogProps,
            title: 'Stop Migration?',
            content: (
                <>
                    <div style={{ display: 'flex', marginBottom: 15, marginTop: 15 }}>
                        <img src={AquantWarningIcon} alt='warning-icon' style={{ marginRight: 5 }} />
                        <Typography variant='subtitle1'>
                            This will Stop the current migration process.
                        </Typography>
                    </div>
                    <Typography variant='body1' style={{marginTop: 15}}>
                        Are you sure you want to proceed?
                    </Typography>
                </>
            ),
            onYes: async () => {
                try {
                    await abortConfigMigration(customer!.name);
                    setErrorMessage(null);
                } catch (error) {
                    console.error('Failed to stop config migration:', error);
                    setErrorMessage('Failed to Stop migration. Please try again.');
                }
                setDialogProps(undefined);
            },
            yesLabel: 'Stop',
            closeLabel: 'No',
        });
    };

    const handleResume = () => {
        setDialogProps({
            ...defaultConfirmationDialogProps,
            title: 'Resume Migration?',
            content: (
                <>
                    <div style={{ display: 'flex', marginBottom: 15, marginTop: 15 }}>
                        <img src={AquantWarningIcon} alt='warning-icon' style={{ marginRight: 5 }} />
                        <Typography variant='subtitle1'>
                            This will resume the migration process.
                        </Typography>
                    </div>
                    <Typography variant='body1' style={{marginTop: 15}}>
                        Are you sure you want to proceed?
                    </Typography>
                </>
            ),
            onYes: async () => {
                try {
                    await resumeConfigMigration(customer!.name);
                    setErrorMessage(null);
                } catch (error) {
                    console.error('Failed to resume config migration:', error);
                    setErrorMessage('Failed to resume migration. Please try again.');
                }
                setDialogProps(undefined);
            },
            yesLabel: 'Resume',
            closeLabel: 'No',
        });
    };

    const handleSwap = () => {
        setDialogProps({
            ...defaultConfirmationDialogProps,
            title: 'Swap Configuration?',
            content: (
                <>
                    <div style={{ display: 'flex', marginBottom: 15, marginTop: 15 }}>
                        <img src={AquantWarningIcon} alt='warning-icon' style={{ marginRight: 5 }} />
                        <Typography variant='subtitle1'>
                            This will swap the current configuration with the migrated one.
                        </Typography>
                    </div>
                    <Typography variant='body1' style={{marginTop: 15}}>
                        Are you sure you want to proceed?
                    </Typography>
                </>
            ),
            onYes: async () => {
                try {
                    await swapConfigMigration(customer!.name);
                    setErrorMessage(null);
                } catch (error) {
                    console.error('Failed to swap config migration:', error);
                    setErrorMessage('Failed to swap configuration. Please try again.');
                }
                setDialogProps(undefined);
            },
            yesLabel: 'Yes',
            closeLabel: 'No',
        });
    };

    const handleCopyNonMigrated = () => {
        setDialogProps({
            ...defaultConfirmationDialogProps,
            title: 'Copy Non-Migrated Documents?',
            content: (
                <>
                    <div style={{ display: 'flex', marginBottom: 15, marginTop: 15 }}>
                        <img src={AquantWarningIcon} alt='warning-icon' style={{ marginRight: 5 }} />
                        <Typography variant='subtitle1'>
                            This will copy all non-migrated documents to the migration process.
                        </Typography>
                    </div>
                    <Typography variant='body1' style={{marginTop: 15}}>
                        Are you sure you want to proceed?
                    </Typography>
                </>
            ),
            onYes: async () => {
                try {
                    await copyNonMigrated(customer!.name);
                    setErrorMessage(null);
                } catch (error) {
                    console.error('Failed to copy non-migrated documents:', error);
                    setErrorMessage('Failed to copy non-migrated documents. Please try again.');
                }
                setDialogProps(undefined);
            },
            yesLabel: 'Yes',
            closeLabel: 'No',
        });
    };

    const handleClearData = () => {
        setClearDataDialogProps([{
            title: 'Clear Migration Data?',
            onClose: () => setClearDataDialogProps(undefined),
            content: (state: any, setState: React.Dispatch<React.SetStateAction<any>>) => (
                <>
                    <div style={{ display: 'flex', marginBottom: 15, marginTop: 15 }}>
                        <img src={AquantWarningIcon} alt='warning-icon' style={{ marginRight: 5 }} />
                        <Typography variant='subtitle1'>
                            This will clear ALL migration data from the migration table, including any original data that was already swapped. This action cannot be undone.
                        </Typography>
                    </div>
                    <Typography variant='body1' style={{marginTop: 15, marginBottom: 15}}>
                        To proceed, please type the customer name <strong>{customer?.name}</strong> in the field below:
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder={`Type "${customer?.name}" to confirm`}
                        value={state?.confirmation || ''}
                        onChange={(e) => setState({ ...state, confirmation: e.target.value })}
                        error={state?.confirmation !== '' && state?.confirmation !== customer?.name}
                        helperText={state?.confirmation !== '' && state?.confirmation !== customer?.name ? 'Customer name does not match' : ''}
                    />
                </>
            ),
            onYes: async (state: any) => {
                if (state?.confirmation !== customer?.name) {
                    return; // Don't proceed if confirmation doesn't match
                }
                try {
                    await clearConfigMigration(customer!.name);
                    setErrorMessage(null);
                } catch (error) {
                    console.error('Failed to clear config migration data:', error);
                    setErrorMessage('Failed to clear migration data. Please try again.');
                }
                setClearDataDialogProps(undefined);
            },
            yesLabel: 'Clear Data',
            closeLabel: 'Cancel',
            yesDisabled: (state: any) => state?.confirmation !== customer?.name,
        }]);
    };

    const isSwapping = configMigrationStatus?.status === 'Swapping';
    const canMigrateCancel = customer && (['None', 'Failed', 'Initializing'].includes(configMigrationStatus?.status || '')) && !isSwapping;
    const canAbortResume = (['InProgress', 'Aborted'].includes(configMigrationStatus?.status || '')) && !isSwapping;
    const canCopyNonMigrated = (['Complete', 'InProgress', 'Aborted'].includes(configMigrationStatus?.status || '')) && 
        (configMigrationStatus?.details?.migration_db?.documents?.not_migrated || 0) > 0 && !isSwapping;
    const canSwap = (['Complete', 'Aborted'].includes(configMigrationStatus?.status || '')) && !isSwapping;
    const canClearData = (['Complete', 'Aborted', 'Failed'].includes(configMigrationStatus?.status || '')) && !isSwapping;    

    const renderDocumentStatus = (documents: any, title: string, showNotMigrated: boolean = false) => (
        <Stack spacing={1}>
            <Typography variant="body2" style={{ color: (documents.uploaded || 0) === 0 ? '#666' : '#2196f3', fontWeight: 'bold' }}>
                Uploaded: {documents.uploaded || 0}
            </Typography>
            <Typography variant="body2" style={{ color: (documents.queued || 0) === 0 ? '#666' : '#1976d2', fontWeight: 'bold' }}>
                Queued: {documents.queued || 0}
            </Typography>
            <Typography variant="body2" style={{ color: (documents.processing || 0) === 0 ? '#666' : '#9c27b0', fontWeight: 'bold' }}>
                Processing: {documents.processing || 0}
            </Typography>
            <Typography variant="body2" style={{ color: (documents.done || 0) === 0 ? '#666' : '#4caf50', fontWeight: 'bold' }}>
                Done: {documents.done || 0}
            </Typography>
            <Typography variant="body2" style={{ color: (documents.failed || 0) === 0 ? '#666' : '#f44336', fontWeight: 'bold' }}>
                Failed: {documents.failed || 0}
            </Typography>
            {showNotMigrated && (
                <Typography variant="body2" style={{ color: (documents.not_migrated || 0) === 0 ? '#666' : '#ff9800', fontWeight: 'bold' }}>
                    Not In Migration DB: {documents.not_migrated || 0}
                </Typography>
            )}
        </Stack>
    );

    const renderConfiguration = (config: any) => (
        <Stack spacing={1}>
            <Typography variant="body2" style={{ color: '#666', fontWeight: 'bold' }}>
                Ingestion Method: {config.pdf_read_method === 'legacy' ? 'basic' : config.pdf_read_method}
            </Typography>
            <Typography variant="body2" style={{ color: '#666', fontWeight: 'bold' }}>
                Embedding Model: {config.embedding_model === 'legacy' ? 'basic' : config.embedding_model}
            </Typography>
        </Stack>
    );

    const renderColumnContent = (dbData: any, title: string, showNotMigrated: boolean = false) => (
        <Stack spacing={2}>
            <Typography variant="subtitle1" style={{ fontWeight: 'bold', textAlign: 'center' }}>{title}</Typography>
            <Divider />
            {renderConfiguration(dbData.config)}
            <Divider />
            {renderDocumentStatus(dbData.documents, title, showNotMigrated)}
        </Stack>
    );

    // Helper function to render current value indicator
    const renderCurrentValueIndicator = (currentValue: string | undefined, label: string) => {
        if (!currentValue) return undefined;
        
        // Map legacy to basic for display
        const displayValue = currentValue === 'legacy' ? 'basic' : currentValue;
        
        return (
            <Stack direction="column" alignItems="stretch" justifyContent="flex-end" spacing={1} height='80px'>
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    padding: '4px 8px',
                    backgroundColor: '#f0f8ff',
                    borderRadius: '4px',
                    border: '1px solid #e3f2fd',
                    fontSize: '0.875rem',
                    height: '56px'
                }}
            >
                <Typography variant="caption" style={{ color: '#666', fontWeight: 'bold' }}>
                    Current {label}:
                </Typography>
                <Typography variant="caption" style={{ color: '#1976d2', fontWeight: 'bold' }}>
                    {displayValue}
                </Typography>
            </Box>
            </Stack>
        );
    };

    const inputItems: InputItem[] = [
        {
            type: 'selection',
            label: "Ingestion Method",
            onChange: (item?: any) => {
                setPdfReadMethod(item)
            },
            items: pdfReadMethodItems(unlockPdfReadMethods),
            isReady: () => !isInProgress,
            showLoading: () => true,
            selectedItem: pdfReadMethod,
            disabled: isInProgress
        },
        {
            type: 'selection',
            label: "Embedding Model",
            onChange: (item?: any) => {
                setEmbeddingModel(item)
            },
            items: embeddingModels,
            isReady: () => !isInProgress,
            showLoading: () => true,
            selectedItem: embeddingModel,
            disabled: isInProgress
        }]
        
    return (
        <Stack padding={2} height='100%' spacing={4}>
            {/* Migration Settings */}
            <Stack direction="row" spacing={2} width='600px'>
                <CopilotInputItems inputItems={inputItems}/>
                <Stack direction="column" alignItems="stretch" width='500px' spacing={2}>
                    {renderCurrentValueIndicator(tenantConfig?.pdf_read_method, 'Method')}
                    {renderCurrentValueIndicator(tenantConfig?.embedding_model, 'Model')}
                </Stack>
            </Stack>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2}>
                <Button 
                    variant="outlined" 
                    onClick={configMigrationStatus?.status === 'Initializing' ? handleCancel : handleMigrate}
                    disabled={!canMigrateCancel}
                >
                    {configMigrationStatus?.status === 'Initializing' ? 'Cancel' : 'Migrate'}
                </Button>
                <Button 
                    variant="outlined" 
                    onClick={configMigrationStatus?.status === 'Aborted' ? handleResume : handleAbort}
                    disabled={!canAbortResume}
                >
                    {configMigrationStatus?.status === 'Aborted' ? 'Resume' : 'Stop'}
                </Button>
                <Button 
                    variant="outlined" 
                    onClick={handleCopyNonMigrated}
                    disabled={!canCopyNonMigrated}
                >
                    Copy Non-Migrated
                </Button>
                <Button 
                    variant="outlined" 
                    onClick={handleSwap}
                    disabled={!canSwap}
                >
                    Swap
                </Button>
                <Button 
                    variant="outlined" 
                    onClick={handleClearData}
                    disabled={!canClearData}
                >
                    Clear Data
                </Button>
            </Stack>

            {/* Migration Status and Status Box */}
            <Box sx={{p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #ccc', color: '#333', flex: 1, position: 'relative'}}>
                {/* Refresh Button */}
                {customer && (
                    <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                        <Tooltip title="Refresh Status">
                            <IconButton 
                                size="small" 
                                onClick={() => customer && fetchConfigMigrationStatus(customer.name)}
                                disabled={isLoadingStatus}
                            >
                                <Refresh />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
                
                {errorMessage && (
                    <Alert severity="error" variant="outlined" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>
                        {errorMessage}
                    </Alert>
                )}
                {!customer ? (
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Please select a tenant to view migration status.</Typography>
                ) :
                    isLoadingStatus ? (
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Loading migration status...</Typography>
                    ) :
                    !configMigrationStatus ? (
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Unknown migration status.</Typography>
                    ) :
                    configMigrationStatus.status === 'None' ? (
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>No migration has been initiated for this tenant.</Typography>
                    ) :
                    configMigrationStatus.status === 'Failed' ? (
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Migration initialization failed.</Typography>
                    ) :
                    configMigrationStatus.status === 'Initializing' ? (
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Migration is being initialized. Please wait...</Typography>
                    ) :
                    configMigrationStatus.status === 'Swapping' ? (
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>Configuration is being swapped. Please wait...</Typography>
                    ) :
                    (['InProgress', 'Complete', 'Aborted'].includes(configMigrationStatus.status) && configMigrationStatus.details) ? (
                        <Grid container spacing={2}>
                            <Grid item xs={5}>
                                {renderColumnContent(configMigrationStatus.details.main_db, 'Main DB')}
                            </Grid>
                            <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Divider orientation="vertical" flexItem />
                            </Grid>
                            <Grid item xs={5}>
                                {renderColumnContent(configMigrationStatus.details.migration_db, 'Migration DB', true)}
                            </Grid>
                        </Grid>
                    ) :
                    'Unknown migration status.'}
            </Box>

            {dialogProps && (
                <StepConfirmation
                    confirmationDialogs={[dialogProps]}
                    finish={() => {}}
                    cancel={() => setDialogProps(undefined)}
                    isValid={true}
                />
            )}
            {clearDataDialogProps && (
                <StepConfirmationWithState
                    confirmationDialogs={clearDataDialogProps}
                    finish={() => {}}
                    cancel={() => setClearDataDialogProps(undefined)}
                    isValid={true}
                />
            )}
        </Stack>
    );
}); 